import { useEffect, useRef } from 'react';
import { useEditor } from './store';
import { read, remove, storage, write } from './storage';

/**
 * Crash-recovery autosave.
 *
 * Snapshots are written to a rotating set of localStorage slots rather than
 * overwriting one key, so a corrupted or half-written save cannot destroy the
 * previous good one. Autosave never touches the user's file — recovery is
 * always an explicit choice.
 */

const SLOT_COUNT = 3;
const SLOT_PREFIX = 'autosave:';
const INDEX_KEY = 'autosave:index';
const INTERVAL_MS = 30_000;

export interface Snapshot {
  savedAt: string;
  documentName: string;
  fileName: string;
  json: string;
}

export function writeSnapshot(snapshot: Snapshot): boolean {
  if (!storage()) return false;
  const next = (Number(read(INDEX_KEY) ?? '0') + 1) % SLOT_COUNT;
  if (!write(`${SLOT_PREFIX}${next}`, JSON.stringify(snapshot))) {
    // Quota exceeded: drop the oldest slot and give up quietly this round.
    remove(`${SLOT_PREFIX}0`);
    return false;
  }
  return write(INDEX_KEY, String(next));
}

export function listSnapshots(): Snapshot[] {
  const out: Snapshot[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) {
    const raw = read(`${SLOT_PREFIX}${i}`);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as Snapshot;
      if (parsed && typeof parsed.json === 'string') out.push(parsed);
    } catch {
      /* ignore an unreadable slot */
    }
  }
  return out.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}

export function clearSnapshots(): void {
  for (let i = 0; i < SLOT_COUNT; i++) remove(`${SLOT_PREFIX}${i}`);
  remove(INDEX_KEY);
}

/** Periodically snapshots the active document while it has unsaved changes. */
export function useAutosave(): void {
  const lastRevision = useRef(-1);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const store = useEditor.getState();
      const tab = store.active();
      if (!tab.dirty || tab.doc.revision === lastRevision.current) return;
      lastRevision.current = tab.doc.revision;
      writeSnapshot({
        savedAt: new Date().toISOString(),
        documentName: tab.doc.name,
        fileName: tab.fileName,
        json: store.projectJson(false),
      });
    }, INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);
}
